import { SESSION_KINDS, SESSION_KIND_LABELS, type SessionKind, type TopicView } from '@algo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { INVALIDATE_ON_WRITE, api, qk } from '../lib/api';
import { formatClock, timer, useElapsedMs, useTimerState } from '../lib/studyTimer';
import { Button, Card, Field, Select } from './ui';

/**
 * Pick what you are studying, hit start, and the elapsed time becomes a
 * LearningSession when you stop — no more guessing at minutes after the fact.
 */
export function StudyTimer({ topics, day }: { topics: TopicView[]; day: string }) {
  const queryClient = useQueryClient();
  const state = useTimerState();
  const elapsed = useElapsedMs(state);
  const running = state.startedAt !== null;

  const minutes = Math.round(elapsed / 60_000);
  // A LearningSession must be at least a minute long, so saving stays disabled
  // until there is something worth recording.
  const canSave = minutes >= 1;

  const save = useMutation({
    mutationFn: () =>
      api.createSession({
        minutes,
        kind: state.kind,
        topicId: state.topicId,
      }),
    onSuccess: async () => {
      timer.reset();
      await Promise.all(
        [...INVALIDATE_ON_WRITE, qk.daily(day)].map((key) =>
          queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
        ),
      );
    },
  });

  const topicName = state.topicId
    ? (topics.find((topic) => topic.id === state.topicId)?.name ?? null)
    : null;

  return (
    <Card
      title="Секундомер"
      subtitle={
        running
          ? `${topicName ?? 'Сэдэвгүй'} · ${SESSION_KIND_LABELS[state.kind]}`
          : 'Юу сурахаа сонгоод эхлүүл'
      }
    >
      <div className="flex flex-col items-center gap-1 py-2">
        <p
          className={clsx(
            'font-mono text-5xl font-semibold tabular-nums transition-colors',
            running ? 'text-accent' : elapsed > 0 ? 'text-ink' : 'text-muted',
          )}
        >
          {formatClock(elapsed)}
        </p>
        <p className="text-xs text-muted">
          {canSave
            ? `${minutes} минут бүртгэгдэнэ`
            : elapsed > 0
              ? 'Хадгалахад дор хаяж 1 минут хэрэгтэй'
              : 'Хараахан эхлээгүй'}
        </p>
      </div>

      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <Field label="Сэдэв">
          <Select
            value={state.topicId ?? ''}
            disabled={running}
            onChange={(event) => timer.setTopic(event.target.value || null)}
          >
            <option value="">Сэдэвгүй</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Төрөл">
          <Select
            value={state.kind}
            disabled={running}
            onChange={(event) => timer.setKind(event.target.value as SessionKind)}
          >
            {SESSION_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {SESSION_KIND_LABELS[kind]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {running ? (
          <Button className="flex-1" onClick={() => timer.pause()}>
            ⏸ Түр зогсоох
          </Button>
        ) : (
          <Button variant="primary" className="flex-1" onClick={() => timer.start()}>
            {elapsed > 0 ? '▶ Үргэлжлүүлэх' : '▶ Эхлүүлэх'}
          </Button>
        )}

        <Button
          variant="primary"
          className="flex-1"
          disabled={!canSave || save.isPending}
          onClick={() => {
            timer.pause();
            save.mutate();
          }}
        >
          {save.isPending ? 'Хадгалж байна…' : '■ Дуусгаад бүртгэх'}
        </Button>

        {elapsed > 0 && (
          <Button variant="ghost" onClick={() => timer.reset()} disabled={save.isPending}>
            Тэглэх
          </Button>
        )}
      </div>

      {save.error && (
        <p className="mt-3 text-sm text-danger">{(save.error as Error).message}</p>
      )}

      <p className="mt-3 text-xs text-muted">
        Хуудсаа сэргээсэн ч, өөр хуудас руу шилжсэн ч цаг үргэлжилнэ.
      </p>
    </Card>
  );
}
